'use client';

import { config } from '@cfg';

import { FC, useState } from 'react';
import { MapSection } from './styles';
import PropertyListingsMap from '@components/PropertyListingsMap/PropertyListingsMap';
import { useAction } from '@util/customHooks/useAction';
import { ListingFilters, ListingSearchResult, SortDir } from '@engelvoelkersamericas/api';
import { AxiosResponse } from 'axios';
import { Center } from '@components/PropertiesSearch';
import { apiClient } from 'api-client';
import { EvaListSortKey } from '@components/PropertiesSearch/PropertySearchFilters';

import { MAP_REQUEST_PAGE_SIZE } from '@util/constants';
import { convertQueryParamsToListingFilters } from '@components/PropertiesSearch/utils';
import { ReadonlyURLSearchParams } from 'next/navigation';

export interface LatLng {
  lat: number;
  lng: number;
}

export interface PolygonListingsMapProps {
  polygonQueryParams: string;
  shopIds: string | null;
}

const PolygonListingsMap: FC<PolygonListingsMapProps> = ({ polygonQueryParams, shopIds }) => {
  const [center, setCenter] = useState<Center | null>(null);
  const initializePolygons = () => {
    const url = new URLSearchParams(polygonQueryParams);
    const lat = url.get('lat');
    const lng = url.get('lng');
    if (lat && lng) {
      setCenter({ lat: lat, lng: lng });
    }
    if (polygonQueryParams) {
      const parsedPolygons = JSON.parse(url.get('polygon') ?? '');

      return parsedPolygons;
    }
    return null;
  };

  const [userPolygons, setUserPolygons] = useState<{ lat: number; lng: number }[][] | null>(
    initializePolygons,
  );

  const { dataMapView: listingDataMapView } = useAction<ListingSearchResult>({
    action: async () => {
      const sortMode = 'priceDesc';
      const listingSortKey = EvaListSortKey[sortMode];
      const listingSortDir = sortMode.endsWith('Desc') ? SortDir.Desc : SortDir.Asc;

      const params = new ReadonlyURLSearchParams(polygonQueryParams);
      const filters = convertQueryParamsToListingFilters(params, null);
      const finalFilters: ListingFilters = {
        ...filters,
        evOnly: true,
        shopIds: shopIds ? shopIds.split(',') : undefined,
      };
      console.log(finalFilters, 'finalFilters');
      // Apply user drawn polygon first if available
      if (userPolygons && userPolygons.length > 0) {
        finalFilters.geoPolygons = userPolygons.map((polygon) => [
          polygon.map((p) => [p.lng, p.lat]),
        ]);
      }

      const fetchListings = async () => {
        return await apiClient.getListings({
          page: 1,
          size: MAP_REQUEST_PAGE_SIZE,
          sortKey: listingSortKey,
          sortDir: listingSortDir,
          listingFilters: finalFilters,
        });
      };

      const handleCoordinateDupes = (res: AxiosResponse<ListingSearchResult, any>) => {
        // Adjust duplicate coordinates before returning listings
        const seen = new Map();
        res.data.listings?.forEach((listing) => {
          if (!listing.Latitude || !listing.Longitude) {
            return;
          }

          // Create a unique key for each lat-lng pair
          let key = `${listing.Latitude},${listing.Longitude}`;

          // Check if the lat-lng pair has already been seen
          while (seen.has(key)) {
            // If duplicate, adjust the lat and lng slightly
            listing.Latitude += 0.00002; // Adjust by a very small increment
            listing.Longitude += 0.00002; // Adjust by a very small increment
            // Update the key to check again
            key = `${listing.Latitude},${listing.Longitude}`;
          }

          // Mark this lat-lng pair as seen
          seen.set(key, true);
        });

        return res;
      };

      let resMapView: AxiosResponse<ListingSearchResult, any> | undefined = undefined;
      let res: AxiosResponse<ListingSearchResult, any>;

      res = await fetchListings();
      resMapView = res;

      res = handleCoordinateDupes(res);
      if (resMapView) {
        resMapView = handleCoordinateDupes(resMapView);
      }

      return { data: res?.data, dataMapView: resMapView?.data };
    },
    manual: false,
  });

  if (userPolygons && userPolygons.length === 0) {
    return <div>Invalid Polygon</div>;
  }

  return (
    <MapSection>
      <PropertyListingsMap
        userPolygon={userPolygons ? userPolygons[0] : null}
        listings={listingDataMapView ? listingDataMapView.listings : []}
        mapPropertyLoader={false}
        config={config}
        center={center}
        zoomValue={6}
        onPolygonChange={(polygon) => setUserPolygons(polygon ? [polygon] : null)}
      />
    </MapSection>
  );
};

export default PolygonListingsMap;
